// @ts-check
import api from './api'
import { assoc, filter, findIndex, forEach, isEmpty, keys, lens, map, or, over, pluck, prop, propEq, propIs, reject, remove } from 'ramda'
import Debug from 'debug'
const debug = Debug('app:shared:users')
import algoliasearchHelper from 'algoliasearch-helper'
import algoliasearch from 'algoliasearch'
import { QualificationsEnum, getDateFromISOString } from './reference'
import { DrugQualificationsEnum, QUALIFICATION, LegacyDrugCertificationsEnum } from '../component-templates/certification-badge/constants'

const colorLens = lens(prop('avatarColor'), assoc('avatarColor'))

/**
 * Normalizes a date string to a UTC Date object
 * @param {string} dateString - ISO date string
 * @returns {Date} - Normalized UTC Date object
 */
const normalizeDate = (dateString) => {
  const date = getDateFromISOString(dateString);
  return new Date(Date.UTC(date.year, date.month - 1, date.day, 12));
};

/**
 * @typedef {Object} Verification
 * @property {string|null} verifiedAt - ISO 8601 date string
 * @property {string|null} verifiedBy - Identifier of the verifier
 * @property {'ADMIN'|'USER'|'ADMIN_LEGACY'|'EXTERNAL'|null} verifiedType - Type of verification
 */

/**
 * @typedef {Object} ProcessedVerification
 * @property {Date|null} verifiedAt 
 * @property {string|null} verifiedBy - Identifier of the verifier
 * @property {'ADMIN'|'USER'|'ADMIN_LEGACY'|'EXTERNAL'|null} verifiedType - Type of verification
 */


/**
* @typedef {Object} QualificationDetails
* @property {'string'} _object_type - Type of the qualification details
* @property {number|null} [level] - Level of the background check
*/

/**
* @typedef {Object} UserQualification
* @property {string} qualificationId - Unique identifier for the qualification
* @property {string} qualificationName - Name of the qualification
* @property {('BACKGROUND_CHECK'|'CERTIFICATION'|'DRUG_SCREEN'|null)} qualificationType - Type of the qualification
* @property {string|null} expiresOn - ISO 8601 date string
* @property {'COMPLETE'|'PENDING'|'INVALID'|null} status - Status of the qualification
* @property {Verification|null} verification - Verification details
* @property {QualificationDetails} details - Additional details of the qualification
* @property {string|null} notes - Additional notes or null if none
*/

/**
 * @typedef {Object} ProcessedUserQualification
 * @property {string} qualificationId - Unique identifier for the qualification
 * @property {string} qualificationName - Name of the qualification
 * @property {('BACKGROUND_CHECK'|'CERTIFICATION'|'DRUG_SCREEN'|null)} qualificationType - Type of the qualification
 * @property {string|null} expiresOn - ISO 8601 date string
 * @property {'COMPLETE'|'PENDING'|'INVALID'|null} status - Status of the qualification
 * @property {ProcessedVerification|null} verification - Verification details
 * @property {QualificationDetails} details - Additional details of the qualification
 * @property {string|null} notes - Additional notes or null if none
 * @property {string} icon - The URL of the icon for this qualification type
 */

/**
 * @typedef {Object} UsersModule
 * @property {(externalUserId: string) => Promise<ProcessedUserQualification[]>} getUserQualifications
 */

/**
 * @param {Object} $http - Angular $http service
 * @returns {UsersModule}
 */
const users = ($http, $q, $mdColorPalette, $window, Auth) => {
  'ngInject'

  let workforce = []
  let skills = []
  let profiles = {}
  let allProfiles = []
  const colors = keys($mdColorPalette)
  let me = Auth.me
  let client
  /**
   * Associate each user with a random avatar color in case they don't have
   * a profile pic
   */
  const getAvatarColor = () => colors[Math.floor(Math.random() * colors.length)]

  const setAllProfiles = (data) => {
    allProfiles = data
    return data
  }

  const getSelected = (isApplicant) => pluck('external_id', filter(prop('selected'), (isApplicant ? allProfiles : workforce)))
  const getUnrated = () => reject(propIs(Number, 'ratingId'), (workforce))
  const getFavorites = () => reject(propEq('favorite', 0), (workforce))
  const getNosends = () => reject(propEq('dont_sendor', null), (workforce))

  const removeWorker = (id) => () => {
    let index = findIndex(propEq('external_id', id), workforce)
    workforce = remove(index, 1, workforce)
  }

  const addNote = (userId, note, isReview) => {
    return $http({
      url: `${api.getBase()}/notes/record`,
      method: 'POST',
      data: {
        userId: userId,
        note: note,
        isReview: isReview || false
      }
    })
  }

  const addWorkHistoryRecord = (userId, company, title, skills, endDate, length) => {
    return $http({
      url: `${api.getBase()}/users/${userId}/previousjobs`,
      method: 'POST',
      data: {
        company_name: company,
        title: title,
        skills: skills,
        end_date: endDate,
        length: length
      }
    })
  }

  const deleteWorkHistory = (userId, id) => {
    return $http({
      url: `${api.getBase()}/users/${userId}/previousjobs`,
      method: 'PATCH',
      data: {
        id: id
      }
    })
  }

  const editWorkHistory = (userId, id, company, title, skills, endDate, length) => {
    return $http({
      url: `${api.getBase()}/users/${userId}/previousjobs`,
      method: 'PUT',
      data: {
        id: id,
        company_name: company,
        title: title,
        skills: skills,
        end_date: endDate,
        length: length
      }
    })
  }

  const getWorkHistory = (userId) => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/users/${userId}/previousjobs`
    })
    .then((result) => { return result['data'] }, (err) => { throw err })
  }

  const getEmploymentReport = (id) => {
    return $http({
      url: `${api.getBase()}/users/${id}/sendEmploymentReport`,
      method: 'GET'
    })
    .then(result => {
      return result['data']
    })
  }

  const retrieveI9Documents = (id) => {
    return $http({
      url: `${api.getBase()}/users/${id}/retrieveI9Docs`,
      method: 'GET'
    })
    .then(result => {
      return result['data']
    })
  }

  const getUserProfile = (id) => {
    return $http({
      url: `${api.getBase()}/users/${id}/profile`,
      method: 'GET'
    })
    .then(result => {
      return result['data']
    })
  }

  const downloadResume = (id) => {
    return $http({
      method: 'POST',
      contentType: 'application/json',
      url: `${api.getBase()}/users/${id}/resume`,
      responseType: 'arraybuffer'
    })
  }

  const fetchProfiles = (region) => {
    return $http({
      url: `${api.getBase()}/user`,
      method: 'GET',
      params: {
        status: 'ENTERED_INTERESTS',
        hasApplied: true,
        region: region
      }
    })
    .then(prop('data'))
    .then(map(over(colorLens, getAvatarColor)))
    .then(setAllProfiles)
  }

  const uploadProfilePic = (data) => {
    debug('update company %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/files/upload/profilepicture`,
      data: {
        profilePicture_base64: data.profilePicture_base64
      }
    })
    .then(null, (err) => { throw err })
  }

  const updateUser = (data) => {
    debug('update company %o', data)

    delete data.userInfo['permissions']
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/update/profile`,
      data: {
        userInfo: data.userInfo
      }
    })
    .then(null, (err) => { throw err })
  }

  const getNotes = (userId) => {
    if (profiles[userId] && profiles[userId].notes) {
      let def = $q.defer()
      def.resolve(profiles[userId].notes)
      return def.promise
    }

    return fetchNotes(userId)
  }

  const getWorkforce = (excludeDsb = false) => {
    // Pull from cache if we have anything, otherwise fetch.
    if (workforce.length) {
      return Promise.resolve(workforce)
    }
    return $http({
      method: 'GET',
      url: `${api.getBase()}/routes/company-workers`,
      params: {
        excludeDsb: excludeDsb
      }
    })
    .then(res => res.data, (err) => { throw err })
    .then(map(over(colorLens, getAvatarColor)))
    .then(setWorkforce)
  }

  const hide = (userId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/hideUser`,
      data: {
        userId
      }
    })
    .then(removeWorker(userId), (err) => { throw err })
  }

  const dontSend = (userId, reason, dsbRating, categoryId, jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/ratings/dontSendBack`,
      data: {
        userId,
        reason,
        dsbRating,
        categoryId,
        jobId
      }
    })
    .then(removeWorker(userId), (err) => { throw err })
  }

  const dontSendNoRate = (userId, note) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/ratings/dontSendBack`,
      data: {
        userId: userId,
        reason: note,
        categoryId: 11,
        note: note,
        addRating: false
      }
    })
    .then(removeWorker(userId), (err) => { throw err })
  }

  const rate = (rating, workers, jobId) => {
    const workerIds = or(workers, getSelected())

    const ratingsArray = map(userId => {
      return {
        rating,
        userId,
        companyId: me.company_id,
        jobId
      }
    }, workerIds)

    return $http({
      method: 'POST',
      url: `${api.getBase()}/ratings/record`,
      data: {
        ratingsArray
      }
    })
  }

  const sendJob = (workers, jobId) => {
    const userIds = or(workers, getSelected())

    return $http({
      method: 'POST',
      url: `${api.getBase()}/job/send`,
      data: {
        userIds: userIds,
        internal: true,
        jobId: jobId
      }
    })
  }

  const sendPipeline = (userIds, pipelineId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/pipeline/send`,
      data: {
        pipelineId,
        userIds
      }
    })
  }

  const sendBatchPipeline = (pipelineId, sendEligibles) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/pipeline/batchSend`,
      data: {
        pipelineId,
        sendEligibles
      }
    })
  }

  const putOnJob = (userIds, jobId, lateStartDate, approveHours) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/putOnJob`,
      data: {
        userIds: userIds,
        jobId: jobId,
        lateStartDate: lateStartDate || null,
        approveHours: approveHours || null
      }
    })
  }

  const clientPutOnJob = (userIds, jobId, lateStartDate, approveHours) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/clientPutOnJob`,
      data: {
        userIds: userIds,
        jobId: jobId,
        lateStartDate: lateStartDate || null,
        approveHours: approveHours || null
      }
    })
  }

  const select = (worker) => {
    let index = findIndex(x => x.external_id === worker.external_id, workforce)
    workforce[index].selected = !workforce[index].selected
  }

  const selectProfiles = (worker, isApplicant) => {
    let index = findIndex(x => x.external_id === worker.external_id, allProfiles)
    allProfiles[index].selected = !allProfiles[index].selected
  }

  const selectAll = forEach(select)
  const selectAllProfiles = forEach(selectProfiles)

  const deselectAll = (isApplicant) => forEach(x => { x.selected = false }, (isApplicant ? allProfiles : workforce))

  const setWorkforce = function (data) {
    workforce = data
    return workforce
  }
  const fetchOtherWorkforce = (companyId) => {
    if (!companyId) {
      if (me.length) {
        companyId = me.companyId || null
      } else {
        Auth.getDetails().then((data) => {
          me = data
        })
      }
    }
    return $http({
      method: 'GET',
      url: `${api.getBase()}/companies/${companyId}/workers`
    })
    .then(res => res.data, (err) => { throw err })
    .then(map(over(colorLens, getAvatarColor)))
    .then(setWorkforce)
  }

  const favoritedByCompanies = (id) => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/users/${id}/favoritedByCompanies`
    })
    .then(result => {
      return result['data']
    })
  }

  const favorite = (userIds, jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/ratings/addFavorite`,
      data: {
        jobId,
        userIds
      }
    })
    .then((res) => {
      userIds.forEach((id) => {
        if (profiles[id]) {
          profiles[id].favorite = true
        }
        if (!isEmpty(workforce)) {
          let index = findIndex(x => id === x.external_id, workforce)
          workforce[index].favorite = 1
        }
      })
    })
  }

  const unfavorite = (userIds) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/ratings/removeFavorite`,
      data: {
        userIds
      }
    })
    .then((res) => {
      userIds.forEach((id) => {
        if (profiles[id]) {
          profiles[id].favorite = false
        }
        let index = findIndex(x => id === x.external_id, workforce)
        workforce[index].favorite = false
      })
    })
  }

  const getProfile = (userId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/routes/profileDetails`,
      data: {
        userId
      }
    })
    .then(prop('data'), (err) => { throw err })
  }

  const fetchNotes = (userId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/notes/get`,
      data: {
        userId
      }
    })
    .then(prop('data'), (err) => { throw err })
    .then(notes => {
      if (profiles[userId]) profiles[userId].notes = notes
      return notes
    })
  }

  const fetchSupervisors = (data) => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/user/getNewSupervisors`
    })
    .then(prop('data'))
  }

  const addSupervisor = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/addSupervisor`,
      data
    })
    .then(prop('data'))
  }

  const updateSupervisor = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/updateSupervisor`,
      data
    })
    .then(prop('data'))
  }

  const updatePassword = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/noauth/user/requestPasswordReset`,
      data
    })
  }

  const checkToken = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/noauth/user/checkTempAuthToken`,
      data
    })
    .then(prop('data'))
  }

  const fetchApplication = (userId) => {
    return $http({
      url: `${api.getBase()}/user/${userId}/application`,
      params: {
        includeTypeform: true
      }
    })
    .then(prop('data'))
  }

  const updateMe = (key, value) => {
    if (me.hasOwnProperty(key)) {
      me[key] = value
    }
  }

  // admin employee user update
  const updateEmployeeInfo = (data) => {
    return $http({
      url: `${api.getBase()}/user/update/statusAndContactInfo`,
      method: 'POST',
      contentType: 'application/json',
      data: {
        userInfo: data
      }
    })
    .then(prop('data'), (err) => { throw err })
  }

  const checkOverlap = (data) => {
    return $http({
      url: `${api.getBase()}/user/checkOverlap`,
      method: 'POST',
      data
    })
  }
  const approveSkills = (data) => {
    return $http({
      url: `${api.getBase()}/user/update/approvedSkills`,
      method: 'POST',
      contentType: 'application/json',
      data: data
    })
  }

  const getSkills = () => {
    if (skills.length) {
      debug('pulling skills information from cache %o')
      let def = $q.defer()
      def.resolve(skills)
      return def.promise
    }
    return fetchSkills()
  }

  const getVerifiedSkills = (id) => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/users/${id}/verifiedskills`
    })
    .then(prop('data'))
  }

  const getCertificationsAdmin = (externalId) => {
    return $http({
      method: 'GET',
      url: `${api.getBaseCMOS()}/user/certifications/${externalId}/admin`
    })
    .then((result) => {
      const certifications = result.data;

      for (const certification of certifications) {
        if (certification.completedOn) {
          const date = getDateFromISOString(certification.completedOn)
          certification.completedOn = new Date(Date.UTC(date.year, date.month - 1, date.day, 12))
        }
        if (certification.expiresOn) {
          const date = getDateFromISOString(certification.expiresOn)
          certification.expiresOn = new Date(Date.UTC(date.year, date.month - 1, date.day, 12))
        }
      }

      const legacyDrugScreenIds = new Set(Object.values(LegacyDrugCertificationsEnum));

      const drugScreenCertifications = certifications.filter(cert => {
        return legacyDrugScreenIds.has(cert.certificateId.toString()) 
      })

      const nonDrugScreenCertifications = certifications.filter(cert => {
        return !legacyDrugScreenIds.has(cert.certificateId.toString()) 
      })

      return [nonDrugScreenCertifications, drugScreenCertifications]
    })
  }

  /**
   * @param {string} externalUserId 
   * @returns {Promise<ProcessedUserQualification[]>}
   */
  const getUserQualifications = (externalUserId) => {
    return $http({
      method: 'GET',
      url: `${api.getBaseCMOS()}/qualifications/${externalUserId}`
    })
    .then((result) => {
      /**
      * @type {UserQualification[]}
      */
      const qualifications = result.data.qualifications;
      
      /**
      * Processes a qualification by adding type, icon, and normalizing dates
      * @param {UserQualification} qualification - The qualification to process
      * @param {typeof QUALIFICATION.TYPES.DRUG_SCREEN|typeof QUALIFICATION.TYPES.BACKGROUND_CHECK} type - The type of qualification
      * @param {string} iconUrl - The URL of the icon for this qualification type
      */
      const processQualification = (qualification, type, iconUrl) => ({
        ...qualification,
        qualificationType: type,
        icon: iconUrl,
        verification: {
          ...qualification.verification,
          verifiedAt: qualification.verification.verifiedAt 
            ? normalizeDate(qualification.verification.verifiedAt) 
            : null
        },
        expiresOn: qualification.expiresOn ? normalizeDate(qualification.expiresOn) : null
      });

      const backgroundCheckIds = new Set(Object.values(QualificationsEnum));
      const drugScreenIds = new Set(Object.values(DrugQualificationsEnum));

      const drugScreenQualifications = qualifications
        .filter(qual => drugScreenIds.has(qual.qualificationId) && qual.status === 'COMPLETE')
        .map(qual => processQualification(qual, QUALIFICATION.TYPES.DRUG_SCREEN, QUALIFICATION.ICON_URLS.DRUG_SCREEN));

      const backgroundCheckQualifications = qualifications
        .filter(qual => backgroundCheckIds.has(qual.qualificationId) && qual.status === 'COMPLETE')
        .map(qual => processQualification(qual, QUALIFICATION.TYPES.BACKGROUND_CHECK, QUALIFICATION.ICON_URLS.BACKGROUND_CHECK));

      return [...drugScreenQualifications, ...backgroundCheckQualifications];
    })
  }

  /** 
   * @param {string} externalUserId 
   * @param {Qualification} data 
   */
  const createUserQualifications = (externalUserId, data) => {
    return $http({
      method: 'POST',
      url: `${api.getBaseCMOS()}/qualifications/${externalUserId}`,
      contentType: 'application/json',
      data,
    })
    .then(prop('data'))
  }

  /**
   * @typedef {Object} Qualification
   * @property {Object[]} qualifications - An array of qualification objects
   * @property {string} qualifications[].qualificationId - The unique identifier of the qualification
   * @property {string} qualifications[].qualificationName - The name of the qualification
   * @property {('BACKGROUND_CHECK'|'CERTIFICATION'|'DRUG_SCREEN')} qualifications[].qualificationType - The type of the qualification
   * @property {string} qualifications[].expiresOn - The expiration date of the qualification (date-time format)
   * @property {Object} qualifications[].verification - The verification details of the qualification
   * @property {string} qualifications[].verification.verifiedAt - The timestamp when the qualification was verified (date-time format)
   * @property {string} qualifications[].verification.verifiedBy - The identifier of the entity that verified the qualification
   * @property {('USER'|'ADMIN'|'ADMIN_LEGACY'|'EXTERNAL')} qualifications[].verification.verifiedType - The type of the entity that verified the qualification
   * @property {*} qualifications[].details - Additional details about the qualification
   * 
   * @param {string} externalUserId 
   * @param {Qualification} data 
   */
  const updateUserQualifications = (externalUserId, data) => {
    return $http({
      method: 'PATCH',
      url: `${api.getBaseCMOS()}/qualifications/${externalUserId}`,
      contentType: 'application/json',
      data,
    })
    .then(prop('data'))
  }

  const updateCertificationsAdmin = (externalId, certs) => {
    return $http({
        method: 'PATCH',
        url: `${api.getBaseCMOS()}/user/certifications/${externalId}/admin`,
        contentType: 'application/json',
        data: certs
    })
  }

  const fetchSkills = () => {
    return $http({
      method: 'GET',
      url: `${api.getBase()}/skills/get`
    })
    .then(prop('data'))
  }

  const getRatings = (userId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/ratings/getAll`,
      data: {
        userId: userId
      }
    })
    .then(prop('data'))
  }

  const getJobActivity = (userId, applied, limit) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/getJobActivity`,
      data: {
        userId: userId,
        applied: applied,
        limit: limit
      }
    })
    .then(prop('data'))
  }

  const getTimeline = (userId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/timeline/user`,
      data: {
        userId: userId
      }
    })
    .then(prop('data'))
  }

  const getPoints = (userId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/getPoints`,
      data: {
        userId: userId
      }
    })
    .then(prop('data'))
  }

  const userSearch = (hitsPerPage) => {
    if (!Auth.algoliaSearchKey) {
      return null
    }

    client = algoliasearch(CONFIG.ALGOLIA_APP_ID, Auth.algoliaSearchKey) // eslint-disable-line
    return algoliasearchHelper(client, CONFIG.ALGOLIA_INDEX, { // eslint-disable-line
      facets: [
        'region',
        'status',
        'certifications',
        'skills',
        'dsbs',
        'address.city',
        'address.state'
      ],
      hitsPerPage: hitsPerPage || 10
    })
  }

  const batchAction = (searchParams, actionType) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/algolia/batchActions`,
      data: {
        query: searchParams.query,
        filters: searchParams.facetsRefinements,
        actionType: actionType
      }
    })
    .then(prop('data'))
  }

  const addUserToWorkforce = (data) => {
    return $http({
      url: `${api.getBase()}/company/addUserToWorkforce`,
      method: 'POST',
      contentType: 'application/json',
      data: {
        userId: data.userId,
        companyId: data.companyId
      }
    })
    .then(prop('data'))
  }

  const getCheckrData = (candidateId) => {
    return $http({
      url: `${api.getBase()}/background/getData`,
      method: 'GET',
      contentType: 'application/json',
      params: {
        candidateId: candidateId
      }
    })
    .then(prop('data'))
  }

  const runBackgroundCheck = (data) => {
    return $http({
      url: `${api.getBase()}/users/${data.userId}/runBackgroundCheck`,
      method: 'POST',
      contentType: 'application/json'
    })
    .then(prop('data'))
  }

  const runMVRCheck = (data) => {
    return $http({
      url: `${api.getBase()}/background/runMVR`,
      method: 'POST',
      contentType: 'application/json',
      headers: {
        'UserId': data.userId
      },
      data: {
        licenseNumber: data.licenseNumber,
        licenseState: data.licenseState
      }
    })
    .then(prop('data'))
  }

  const createNewHire = (data, region) => {
    return $http({
      url: `${api.getBase()}/background/createNewHire`,
      method: 'POST',
      contentType: 'application/json',
      data: {
        candidateData: data,
        regionName: region
      }
    })
    .then(prop('data'))
  }

  const removeMark = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/company/removeMark`,
      data: {
        markId: data.markId,
        isFave: data.isFave,
        userExternalId: data.userExternalId
      }
    })
    .then(prop('data'))
  }

  const getSentJobsCSV = (userId) => {
    let url = `${api.getBase()}/user/getJobActivity?&userId=${userId}&applied=false`
    return $http({
      method: 'POST',
      url
    })
  }

  const addApplicationStage = (data) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/users/${data.userId}/applicationStages`,
      data: {
        stageId: data.stageId
      }
    })
    .then(null, (err) => { throw err })
  }

  const resetApplicationStage = (data) => {
    return $http({
      method: 'PUT',
      url: `${api.getBase()}/users/${data.userId}/applicationStages`,
      data: {
        stageId: data.stageId
      }
    })
    .then(null, (err) => { throw err })
  }

  /* users can't update phone # or address without approval */
  const submitUserContactInfoForApproval = (data) => {
    debug('submit user info for approval %o', data)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/update/singleField`,
      data: data
    })
    .then(null, (err) => { throw err })
  }

  const getBankAccounts = (userId) => {
    debug('get user bank accounts %o', userId)
    return $http({
      method: 'GET',
      url: `${api.getBase()}/users/${userId}/bankAccounts`
    })
    .then((result) => { return result['data'] }, (err) => { throw err })
  }

  const activateBankAccount = (userId, bankAccountId) => {
    debug('activate user bank account %o', userId)
    return $http({
      method: 'PUT',
      url: `${api.getBase()}/users/${userId}/banks/${bankAccountId}/activate`
    })
    .then(null, (err) => { throw err })
  }

  const deleteBankAccount = (userId, bankAccountId) => {
    debug('delete user bank account %o', userId)
    return $http({
      method: 'DELETE',
      url: `${api.getBase()}/users/${userId}/banks/${bankAccountId}/delete`
    })
    .then(null, (err) => { throw err })
  }

  const disableDirectDeposit = (userId) => {
    debug('disable direct deposit for user %o', userId)
    return $http({
      method: 'POST',
      url: `${api.getBase()}/users/${userId}/disableDirectDeposit`
    })
    .then(null, (err) => { throw err })
  }

  const getSkillsQuizzes = (id) => {
    return $http({
      url: `${api.getBase()}/users/${id}/applicationStages`,
      method: 'GET'
    })
    .then(prop('data'))
  }

  const getLegacyI9 = (id) => {
    return $http({
      url: `${api.getBase()}/users/${id}/pre2018NewHires`,
      method: 'POST',
      responseType: 'arraybuffer'
    })
  }

  const getPerformanceData = (userIds) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/getPerformanceData`,
      data: {
        userIds: userIds
      }
    }).then(prop('data'), (err) => { throw err })
  }

  const getPerformanceScore = (userIds, jobId) => {
    return $http({
      method: 'POST',
      url: `${api.getBase()}/user/getPerformanceScore`,
      data: {
        userIds: userIds,
        jobId: jobId
      }
    }).then(prop('data'), (err) => { throw err })
  }

  const bypassDuplicateSsn = (userId) => {
    return $http({
      method: 'PATCH',
      url: `${api.getBaseCMOS()}/user/${userId}/bypassDuplicateSsn/admin`
    })
  }

  const updateUserProfileAdmin = (userId, userProfileInfo, userAddress) => {
    return $http({
      method: 'PATCH',
      url: `${api.getBaseCMOS()}/user/${userId}/profile/admin`,
      data: {
        firstName: userProfileInfo?.firstName,
        lastName: userProfileInfo?.lastName,
        emailAddress: userProfileInfo?.emailAddress?.toLowerCase() ?? null,
        phone: userProfileInfo?.phone,
        status: userProfileInfo?.status,
        address: userAddress ? {
          ...userAddress,
          // We'll always want to send USA as the country until we launch internationally
          country: "USA",
        } : null
      }
    })
  }

  return {
    activateBankAccount,
    addApplicationStage,
    addUserToWorkforce,
    addWorkHistoryRecord,
    editWorkHistory,
    deleteWorkHistory,
    downloadResume,
    approveSkills,
    getCertificationsAdmin,
    getCheckrData,
    createNewHire,
    clientPutOnJob,
    deleteBankAccount,
    disableDirectDeposit,
    getWorkforce,
    getWorkHistory,
    favorite,
    fetchSupervisors,
    fetchOtherWorkforce,
    addSupervisor,
    updateSupervisor,
    favoritedByCompanies,
    getBankAccounts,
    getProfile,
    getUnrated,
    getRatings,
    getNotes,
    getFavorites,
    getNosends,
    getLegacyI9,
    getEmploymentReport,
    batchAction,
    getSkills,
    getVerifiedSkills,
    getSentJobsCSV,
    updateUser,
    uploadProfilePic,
    addNote,
    unfavorite,
    getSelected,
    select,
    selectAll,
    selectAllProfiles,
    deselectAll,
    rate,
    resetApplicationStage,
    runBackgroundCheck,
    runMVRCheck,
    retrieveI9Documents,
    hide,
    dontSend,
    sendPipeline,
    sendBatchPipeline,
    sendJob,
    putOnJob,
    getJobActivity,
    getTimeline,
    getPoints,
    getSkillsQuizzes,
    dontSendNoRate,
    removeWorker,
    removeMark,
    updatePassword,
    checkToken,
    updateMe,
    updateEmployeeInfo,
    getUserProfile,
    fetchProfiles,
    fetchApplication,
    checkOverlap,
    userSearch,
    submitUserContactInfoForApproval,
    getPerformanceData,
    getPerformanceScore,
    bypassDuplicateSsn,
    updateUserProfileAdmin,
    updateCertificationsAdmin,
    getUserQualifications,
    createUserQualifications,
    updateUserQualifications,
  }
}

export default users
